<template>
  <div class="admin-help">
    <button
      type="button"
      class="admin-help-trigger"
      :aria-label="`Open ${topic.title}`"
      @click="open = true"
    >
      <i class="bi bi-question-circle" aria-hidden="true"></i>
      <span>Help</span>
    </button>

    <teleport to="body">
      <div
        v-if="open && presentation !== 'floating'"
        :class="backdropClasses"
        role="presentation"
        @click.self="open = false"
      >
        <component
          :is="presentation === 'modal' ? 'section' : 'aside'"
          :class="panelClasses"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <header class="admin-help-header">
            <div>
              <p v-if="topic.eyebrow" class="admin-help-eyebrow">
                {{ topic.eyebrow }}
              </p>
              <h2 :id="titleId">{{ topic.title }}</h2>
            </div>
            <button
              type="button"
              class="admin-help-close"
              aria-label="Close help"
              @click="open = false"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </header>

          <p v-if="topic.summary" class="admin-help-summary">
            {{ topic.summary }}
          </p>

          <section
            v-for="section in topic.sections"
            :key="section.heading"
            class="admin-help-section"
          >
            <h3>{{ section.heading }}</h3>
            <p v-if="section.body">{{ section.body }}</p>
            <div
              v-if="section.promptMap"
              class="admin-help-prompt-map"
              :aria-label="section.promptMap.label"
            >
              <h4>{{ section.promptMap.title }}</h4>
              <div class="prompt-map-stage is-start">
                <strong>{{ section.promptMap.start.label }}</strong>
                <div class="prompt-map-chips">
                  <code
                    v-for="chip in section.promptMap.start.chips"
                    :key="chip"
                  >
                    {{ chip }}
                  </code>
                </div>
              </div>
              <div class="prompt-map-connector"></div>
              <div class="prompt-map-branches">
                <div
                  v-for="branch in section.promptMap.branches"
                  :key="branch.prompt"
                  class="prompt-map-branch"
                >
                  <span>{{ branch.condition }}</span>
                  <strong>{{ branch.prompt }}</strong>
                  <small>{{ branch.detail }}</small>
                </div>
              </div>
              <div class="prompt-map-connector"></div>
              <div class="prompt-map-stage is-context">
                <strong>{{ section.promptMap.context.label }}</strong>
                <div class="prompt-map-chips">
                  <code
                    v-for="chip in section.promptMap.context.chips"
                    :key="chip"
                  >
                    {{ chip }}
                  </code>
                </div>
              </div>
              <div class="prompt-map-connector"></div>
              <div class="prompt-map-stage">
                <strong>{{ section.promptMap.base.label }}</strong>
                <div class="prompt-map-chips">
                  <code
                    v-for="chip in section.promptMap.base.chips"
                    :key="chip"
                  >
                    {{ chip }}
                  </code>
                </div>
              </div>
              <div class="prompt-map-connector"></div>
              <div class="prompt-map-output">
                {{ section.promptMap.output }}
              </div>
              <div class="prompt-map-optional">
                <span
                  v-for="item in section.promptMap.optional"
                  :key="item"
                >
                  {{ item }}
                </span>
              </div>
            </div>
            <div
              v-if="section.diagram"
              class="admin-help-flow"
              :aria-label="section.diagram.label"
            >
              <div
                v-for="node in section.diagram.nodes"
                :key="node.label"
                :class="[
                  'admin-help-flow-node',
                  node.type && `is-${node.type}`,
                ]"
              >
                <strong>{{ node.label }}</strong>
                <span v-if="node.detail">{{ node.detail }}</span>
                <ul v-if="node.items">
                  <li v-for="item in node.items" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
            <ol v-if="section.steps">
              <li v-for="step in section.steps" :key="step">{{ step }}</li>
            </ol>
            <ul v-if="section.bullets">
              <li v-for="bullet in section.bullets" :key="bullet">
                {{ bullet }}
              </li>
            </ul>
            <div v-if="section.callout" class="admin-help-callout">
              {{ section.callout }}
            </div>
            <div v-if="section.tags" class="admin-help-tags">
              <code v-for="tag in section.tags" :key="tag">{{ tag }}</code>
            </div>
          </section>

          <section v-if="visibleScreenshots.length" class="admin-help-section">
            <h3>Screenshots</h3>
            <figure
              v-for="shot in visibleScreenshots"
              :key="shot.src"
              class="admin-help-shot"
            >
              <img :src="shot.src" :alt="shot.alt" />
              <figcaption>{{ shot.caption }}</figcaption>
            </figure>
          </section>
        </component>
      </div>
      <section
        v-if="open && presentation === 'floating'"
        :class="panelClasses"
        :style="floatingStyle"
        ref="floatingPanel"
        role="dialog"
        aria-modal="false"
        :aria-labelledby="titleId"
      >
        <header class="admin-help-header" @mousedown="startDrag">
          <div>
            <p v-if="topic.eyebrow" class="admin-help-eyebrow">
              {{ topic.eyebrow }}
            </p>
            <h2 :id="titleId">{{ topic.title }}</h2>
          </div>
          <button
            type="button"
            class="admin-help-close"
            aria-label="Close help"
            @click="open = false"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <p v-if="topic.summary" class="admin-help-summary">
          {{ topic.summary }}
        </p>

        <section
          v-for="section in topic.sections"
          :key="section.heading"
          class="admin-help-section"
        >
          <h3>{{ section.heading }}</h3>
          <p v-if="section.body">{{ section.body }}</p>
          <div
            v-if="section.promptMap"
            class="admin-help-prompt-map"
            :aria-label="section.promptMap.label"
          >
            <h4>{{ section.promptMap.title }}</h4>
            <div class="prompt-map-stage is-start">
              <strong>{{ section.promptMap.start.label }}</strong>
              <div class="prompt-map-chips">
                <code
                  v-for="chip in section.promptMap.start.chips"
                  :key="chip"
                >
                  {{ chip }}
                </code>
              </div>
            </div>
            <div class="prompt-map-connector"></div>
            <div class="prompt-map-branches">
              <div
                v-for="branch in section.promptMap.branches"
                :key="branch.prompt"
                class="prompt-map-branch"
              >
                <span>{{ branch.condition }}</span>
                <strong>{{ branch.prompt }}</strong>
                <small>{{ branch.detail }}</small>
              </div>
            </div>
            <div class="prompt-map-connector"></div>
            <div class="prompt-map-stage is-context">
              <strong>{{ section.promptMap.context.label }}</strong>
              <div class="prompt-map-chips">
                <code
                  v-for="chip in section.promptMap.context.chips"
                  :key="chip"
                >
                  {{ chip }}
                </code>
              </div>
            </div>
            <div class="prompt-map-connector"></div>
            <div class="prompt-map-stage">
              <strong>{{ section.promptMap.base.label }}</strong>
              <div class="prompt-map-chips">
                <code
                  v-for="chip in section.promptMap.base.chips"
                  :key="chip"
                >
                  {{ chip }}
                </code>
              </div>
            </div>
            <div class="prompt-map-connector"></div>
            <div class="prompt-map-output">
              {{ section.promptMap.output }}
            </div>
            <div class="prompt-map-optional">
              <span
                v-for="item in section.promptMap.optional"
                :key="item"
              >
                {{ item }}
              </span>
            </div>
          </div>
          <div
            v-if="section.diagram"
            class="admin-help-flow"
            :aria-label="section.diagram.label"
          >
            <div
              v-for="node in section.diagram.nodes"
              :key="node.label"
              :class="[
                'admin-help-flow-node',
                node.type && `is-${node.type}`,
              ]"
            >
              <strong>{{ node.label }}</strong>
              <span v-if="node.detail">{{ node.detail }}</span>
              <ul v-if="node.items">
                <li v-for="item in node.items" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
          <ol v-if="section.steps">
            <li v-for="step in section.steps" :key="step">{{ step }}</li>
          </ol>
          <ul v-if="section.bullets">
            <li v-for="bullet in section.bullets" :key="bullet">
              {{ bullet }}
            </li>
          </ul>
          <div v-if="section.callout" class="admin-help-callout">
            {{ section.callout }}
          </div>
          <div v-if="section.tags" class="admin-help-tags">
            <code v-for="tag in section.tags" :key="tag">{{ tag }}</code>
          </div>
        </section>

        <section v-if="visibleScreenshots.length" class="admin-help-section">
          <h3>Screenshots</h3>
          <figure
            v-for="shot in visibleScreenshots"
            :key="shot.src"
            class="admin-help-shot"
          >
            <img :src="shot.src" :alt="shot.alt" />
            <figcaption>{{ shot.caption }}</figcaption>
          </figure>
        </section>
      </section>
    </teleport>
  </div>
</template>

<script>
export default {
  name: "AdminHelpButton",
  props: {
    topic: {
      type: Object,
      required: true,
    },
    presentation: {
      type: String,
      default: "drawer",
      validator: (value) => ["drawer", "modal", "floating"].includes(value),
    },
    floatingWidth: {
      type: String,
      default: "520px",
    },
  },
  data() {
    return {
      open: false,
      uid: Math.random().toString(36).slice(2),
      dragState: null,
      floatingPosition: null,
    };
  },
  computed: {
    titleId() {
      return `admin-help-title-${this.uid}`;
    },
    backdropClasses() {
      return {
        "admin-help-backdrop": true,
        "admin-help-backdrop--modal": this.presentation !== "drawer",
      };
    },
    panelClasses() {
      return {
        "admin-help-panel": true,
        "admin-help-panel--modal": this.presentation === "modal",
        "admin-help-panel--floating": this.presentation === "floating",
      };
    },
    floatingStyle() {
      const baseStyle = {
        "--admin-help-floating-width": this.floatingWidth,
      };
      if (!this.floatingPosition) return baseStyle;
      return {
        ...baseStyle,
        left: `${this.floatingPosition.left}px`,
        right: "auto",
        top: `${this.floatingPosition.top}px`,
      };
    },
    visibleScreenshots() {
      return (this.topic.screenshots || []).filter((shot) => shot.src);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("mousemove", this.onDrag);
    document.addEventListener("mouseup", this.stopDrag);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
  },
  methods: {
    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.open = false;
    },
    startDrag(event) {
      if (this.presentation !== "floating" || event.target.closest("button")) {
        return;
      }

      const panel = this.$refs.floatingPanel;
      if (!panel) return;

      const rect = panel.getBoundingClientRect();
      this.floatingPosition = {
        left: rect.left,
        top: rect.top,
      };
      this.dragState = {
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        width: rect.width,
        height: rect.height,
      };
      event.preventDefault();
    },
    onDrag(event) {
      if (!this.dragState) return;

      const margin = 12;
      const maxLeft = window.innerWidth - this.dragState.width - margin;
      const maxTop = window.innerHeight - this.dragState.height - margin;

      this.floatingPosition = {
        left: Math.min(
          Math.max(margin, event.clientX - this.dragState.offsetX),
          Math.max(margin, maxLeft),
        ),
        top: Math.min(
          Math.max(margin, event.clientY - this.dragState.offsetY),
          Math.max(margin, maxTop),
        ),
      };
    },
    stopDrag() {
      this.dragState = null;
    },
  },
};
</script>

<style scoped>
.admin-help {
  display: inline-flex;
  flex: 0 0 auto;
  width: auto;
}

.admin-help-trigger {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(31, 109, 202, 0.35);
  border-radius: 6px;
  color: var(--primary-color, #1f6dca);
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  line-height: 1;
  min-width: max-content;
  padding: 8px 11px;
  white-space: nowrap;
  width: auto;
}

.admin-help-trigger:hover,
.admin-help-trigger:focus {
  background: rgba(31, 109, 202, 0.08);
  outline: none;
}

.admin-help-backdrop {
  background: rgba(22, 33, 46, 0.35);
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 2500;
}

.admin-help-backdrop--modal {
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.admin-help-panel {
  background: #fff;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
  color: #24313f;
  height: 100%;
  max-width: min(560px, 92vw);
  overflow-y: auto;
  padding: 24px;
  text-align: left;
  width: 560px;
}

.admin-help-panel--modal {
  border-radius: 8px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.22);
  height: auto;
  max-height: min(720px, calc(100vh - 40px));
  max-width: min(680px, calc(100vw - 40px));
  width: 680px;
}

.admin-help-panel--floating {
  border: 1px solid #dce5ef;
  border-radius: 8px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.2);
  height: auto;
  max-height: min(680px, calc(100vh - 32px));
  max-width: min(var(--admin-help-floating-width), calc(100vw - 32px));
  position: fixed;
  right: 24px;
  top: 96px;
  width: var(--admin-help-floating-width);
  z-index: 3600;
}

.admin-help-panel--floating .admin-help-header {
  cursor: move;
}

.admin-help-header {
  align-items: flex-start;
  border-bottom: 1px solid #e2e7ee;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding-bottom: 14px;
}

.admin-help-eyebrow {
  color: #647286;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0 0 4px;
  text-transform: uppercase;
}

.admin-help-header h2 {
  color: #1d2a38;
  font-size: 22px;
  line-height: 1.25;
  margin: 0;
}

.admin-help-close {
  align-items: center;
  background: transparent;
  border: none;
  color: #4f5f70;
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  height: 34px;
  justify-content: center;
  padding: 0;
  width: 34px;
}

.admin-help-close:hover,
.admin-help-close:focus {
  color: #1d2a38;
  outline: none;
}

.admin-help-summary {
  color: #425367;
  font-size: 15px;
  line-height: 1.5;
  margin: 16px 0 20px;
}

.admin-help-section {
  border-top: 1px solid #edf1f5;
  padding: 18px 0 0;
}

.admin-help-section + .admin-help-section {
  margin-top: 18px;
}

.admin-help-section h3 {
  color: #1d2a38;
  font-size: 16px;
  line-height: 1.3;
  margin: 0 0 10px;
}

.admin-help-section p,
.admin-help-section li {
  color: #425367;
  font-size: 14px;
  line-height: 1.48;
}

.admin-help-section p {
  margin: 0 0 10px;
}

.admin-help-section ol,
.admin-help-section ul {
  margin: 0;
  padding-left: 22px;
}

.admin-help-section li + li {
  margin-top: 7px;
}

.admin-help-prompt-map {
  background: #fbfcfe;
  border: 1px solid #dce5ef;
  border-radius: 8px;
  margin: 4px 0 12px;
  padding: 12px;
}

.admin-help-prompt-map h4 {
  color: #1d2a38;
  font-size: 13px;
  line-height: 1.3;
  margin: 0 0 10px;
}

.prompt-map-stage,
.prompt-map-branch,
.prompt-map-output {
  background: #fff;
  border: 1px solid #dce5ef;
  border-radius: 6px;
  padding: 9px 10px;
}

.prompt-map-stage.is-start {
  background: #f7f9fc;
}

.prompt-map-stage.is-context {
  background: #f1f8f5;
  border-color: #cfe7da;
}

.prompt-map-stage strong,
.prompt-map-branch strong {
  color: #1d2a38;
  display: block;
  font-size: 13px;
  line-height: 1.35;
}

.prompt-map-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.prompt-map-chips code {
  background: #f4f7fb;
  border: 1px solid #dce5ef;
  border-radius: 4px;
  color: #29425d;
  font-size: 12px;
  padding: 4px 6px;
}

.prompt-map-connector {
  background: #9badc1;
  height: 14px;
  margin: 0 auto;
  position: relative;
  width: 2px;
}

.prompt-map-connector::after {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #9badc1;
  bottom: -5px;
  content: "";
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}

.prompt-map-branches {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.prompt-map-branch {
  background: #fff9ed;
  border-color: #ecd5a4;
}

.prompt-map-branch span,
.prompt-map-branch small {
  color: #425367;
  display: block;
  font-size: 12px;
  line-height: 1.35;
}

.prompt-map-branch strong {
  margin: 3px 0;
  overflow-wrap: anywhere;
}

.prompt-map-output {
  background: #eef4fb;
  border-color: #c9d9eb;
  color: #1d2a38;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.prompt-map-optional {
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 8px;
}

.prompt-map-optional span {
  background: #fff;
  border: 1px dashed #bdcad8;
  border-radius: 6px;
  color: #526274;
  font-size: 12px;
  line-height: 1.35;
  padding: 7px 8px;
}

.admin-help-flow {
  display: grid;
  gap: 10px;
  margin: 4px 0 2px;
}

.admin-help-flow-node {
  background: #f7f9fc;
  border: 1px solid #dce5ef;
  border-radius: 6px;
  color: #425367;
  padding: 10px 12px;
  position: relative;
}

.admin-help-flow-node::after {
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #9badc1;
  bottom: -9px;
  content: "";
  left: 22px;
  position: absolute;
}

.admin-help-flow-node:last-child::after {
  display: none;
}

.admin-help-flow-node strong {
  color: #1d2a38;
  display: block;
  font-size: 13px;
  line-height: 1.35;
  margin-bottom: 3px;
}

.admin-help-flow-node span,
.admin-help-flow-node li {
  color: #425367;
  font-size: 13px;
  line-height: 1.4;
}

.admin-help-flow-node ul {
  margin: 4px 0 0;
  padding-left: 18px;
}

.admin-help-flow-node.is-decision {
  background: #fff9ed;
  border-color: #ecd5a4;
}

.admin-help-flow-node.is-runtime {
  background: #f1f8f5;
  border-color: #cfe7da;
}

.admin-help-callout {
  background: #f7f9fc;
  border-left: 3px solid var(--primary-color, #1f6dca);
  color: #425367;
  font-size: 14px;
  line-height: 1.45;
  margin-top: 12px;
  padding: 10px 12px;
}

.admin-help-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.admin-help-tags code {
  background: #f4f7fb;
  border: 1px solid #dce5ef;
  border-radius: 4px;
  color: #29425d;
  font-size: 12px;
  padding: 5px 7px;
}

.admin-help-shot {
  border: 1px solid #dce5ef;
  border-radius: 6px;
  margin: 0;
  overflow: hidden;
}

.admin-help-shot + .admin-help-shot {
  margin-top: 12px;
}

.admin-help-shot img {
  display: block;
  width: 100%;
}

.admin-help-shot figcaption {
  background: #f7f9fc;
  color: #526274;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 10px;
}

@media (max-width: 640px) {
  .admin-help-backdrop {
    display: block;
  }

  .admin-help-panel {
    height: 100%;
    max-width: none;
    width: 100%;
  }

  .admin-help-panel--modal {
    border-radius: 0;
    max-height: none;
  }

  .admin-help-panel--floating {
    bottom: 12px;
    left: 12px;
    max-height: calc(100vh - 24px);
    max-width: none;
    right: 12px;
    top: auto;
    width: auto;
  }

  .prompt-map-branches,
  .prompt-map-optional {
    grid-template-columns: 1fr;
  }
}
</style>
